import { useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';

import { TrashIcon } from '@heroicons/react/24/outline';

import { Card } from '../../components/base/Card';
import { Button } from '../../components/base/Button';
import { useTheme } from '../../contexts/ThemeContext';
import { TextField } from '../../components/base/TextField';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useNotification } from '../../hooks/useNotifications';

import 'chartjs-plugin-dragdata';
import AnnotationPlugin from 'chartjs-plugin-annotation';

const xMin = -10;
const xMax = 25;
const yMin = 20;
const yMax = 60;

type ChartPoint = { x: number; y: number; _virtual?: boolean };

interface WeatherCompTableRowProps extends ChartPoint {
  index: number;
  onChange: (index: number, x: number, y: number) => void;
  onRemove: (index: number) => void;
  disableRemove: boolean;
}

function WeatherCompTableRow({
  index,
  x,
  y,
  onChange,
  onRemove,
  disableRemove,
}: WeatherCompTableRowProps) {
  // Local state for input fields as strings
  const [xInput, setXInput] = useState(String(x));
  const [yInput, setYInput] = useState(String(y));

  // Format value: keep minus, strip leading zeros
  const formatValue = (val: string) => {
    if (val === '' || val === '-') return val;
    if (val.startsWith('-')) {
      return '-' + val.slice(1).replace(/^0+(?=\d)/, '');
    }
    return val.replace(/^0+(?=\d)/, '');
  };

  // Handle input change for x
  const handleXChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    // Allow empty or just minus
    if (val === '' || val === '-') {
      setXInput(val);
      return;
    }

    // Remove leading zeros
    val = formatValue(val);
    setXInput(val);

    // Only update if valid integer
    const parsed = parseInt(val, 10);
    if (!isNaN(parsed)) {
      onChange(index, parsed, y);
    }
  };

  // Handle input blur for x (clamp and correct value)
  const handleXBlur = () => {
    let val = xInput;
    if (val === '' || val === '-') {
      setXInput(String(x)); // revert to last valid
      return;
    }
    let parsed = parseInt(val, 10);
    if (isNaN(parsed)) parsed = xMin;
    parsed = Math.max(xMin, Math.min(xMax, parsed));
    setXInput(String(parsed));
    onChange(index, parsed, y);
  };

  // Repeat for y
  const handleYChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '' || val === '-') {
      setYInput(val);
      return;
    }
    val = formatValue(val);
    setYInput(val);

    const parsed = parseInt(val, 10);
    if (!isNaN(parsed)) {
      onChange(index, x, parsed);
    }
  };

  const handleYBlur = () => {
    let val = yInput;
    if (val === '' || val === '-') {
      setYInput(String(y));
      return;
    }
    let parsed = parseInt(val, 10);
    if (isNaN(parsed)) parsed = yMin;
    parsed = Math.max(yMin, Math.min(yMax, parsed));
    setYInput(String(parsed));
    onChange(index, x, parsed);
  };

  useEffect(() => {
    setXInput(String(x));
    setYInput(String(y));
  }, [x, y]);

  return (
    <tr className={clsx(index % 2 !== 0 && 'bg-primary/10')}>
      <td className="whitespace-nowrap px-3 py-4 text-sm">{index + 1}</td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <TextField
          type="number"
          min={xMin}
          max={xMax}
          value={xInput}
          onChange={handleXChange}
          onBlur={handleXBlur}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <TextField
          type="number"
          min={yMin}
          max={yMax}
          value={yInput}
          onChange={handleYChange}
          onBlur={handleYBlur}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <TrashIcon
          className={clsx(
            'w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer',
            disableRemove &&
              'text-gray-500 hover:opacity-70 hover:cursor-not-allowed'
          )}
          onClick={disableRemove ? undefined : () => onRemove(index)}
        />
      </td>
    </tr>
  );
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  AnnotationPlugin
);

export default function WeatherComp() {
  const addNotification = useNotification();
  const { theme } = useTheme();

  const getThemeColors = () =>
    theme.isDark
      ? `${theme.dark.primary.r}, ${theme.dark.primary.g}, ${theme.dark.primary.b}`
      : `${theme.light.primary.r}, ${theme.light.primary.g}, ${theme.light.primary.b}`;

  const initialData = {
    datasets: [
      {
        label: 'Compensation Curve 1',
        data: [
          { x: -3, y: 45 },
          { x: 20, y: 25 },
        ] as ChartPoint[],
        borderColor: `rgb(${getThemeColors()})`,
        backgroundColor: `rgba(${getThemeColors()}, 0.5)`,
        tension: 0,
        showLine: true,
        fill: false,
        pointRadius: (ctx: any) => {
          const point = ctx.raw as ChartPoint;
          return point._virtual ? 0 : 10;
        },
        pointHoverRadius: (ctx: any) => {
          const point = ctx.raw as ChartPoint;
          return point._virtual ? 0 : 12;
        },
      },
    ],
  };

  const [chartData, setChartData] = useState(initialData);

  const getExtendedData = () => {
    const points = chartData.datasets[0].data as ChartPoint[];
    if (points.length === 0) return chartData;

    // Sort points by x
    const sorted = [...points].sort((a, b) => a.x - b.x);

    // Add left edge point with y matching the first point
    const left = { x: xMin - 2, y: sorted[0].y, _virtual: true };
    // Add right edge point with y matching the last point
    const right = {
      x: xMax + 2,
      y: sorted[sorted.length - 1].y,
      _virtual: true,
    };

    // Build new data array
    const extendedPoints = [left, ...sorted, right];

    // Return new chartData with extended points
    return {
      ...chartData,
      datasets: [
        {
          ...chartData.datasets[0],
          data: extendedPoints,
        },
      ],
    };
  };

  const getAnnotationY = () => {
    const points = chartData.datasets[0].data
      .filter((p) => !p._virtual)
      .sort((a, b) => a.x - b.x);

    const exact = points.find((p) => p.x === 0);
    if (exact) return exact.y;

    const before = [...points].reverse().find((p) => p.x < 0);
    const after = points.find((p) => p.x > 0);

    if (before && after) {
      // Linear interpolation
      const t = (0 - before.x) / (after.x - before.x);
      return before.y + t * (after.y - before.y);
    }

    if (!before && after) {
      // If no point before 0, return the y of the first point after 0
      return after.y;
    }

    if (before && !after) {
      // If no point before 0, return the y of the first point after 0
      return before.y;
    }

    // If 0 is outside the range of available points
    return null;
  };

  const data = useMemo(() => getExtendedData(), [chartData]);

  const options = {
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        displayColors: false,
        callbacks: {
          title: () => [],
          label: function (context: any) {
            const x = context.parsed.x;
            const y = context.parsed.y;
            return [`Outdoor Temp: ${x}°C`, `Flow Temp: ${y}°C`];
          },
        },
      },
      title: { display: false },
      dragData: {
        round: 0,
        showTooltip: true,
        dragX: true,
        onDrag: function (
          _: any,
          datasetIndex: number,
          index: number,
          value: ChartPoint
        ) {
          // Clamp x/y to min/max
          value.x = Math.max(xMin, Math.min(xMax, value.x));
          value.y = Math.max(yMin, Math.min(yMax, value.y));

          // Get dataset
          const points = data.datasets[datasetIndex].data as ChartPoint[];

          // Don't allow dragging virtual points
          if (points[index]._virtual) {
            return false;
          }

          // is the first or last movable point
          if (index === 1) {
            points[0] = { ...points[0], y: value.y };
          } else if (index === points.length - 2) {
            points[points.length - 1] = {
              ...points[points.length - 1],
              y: value.y,
            };
          }

          // Find neighbors (skip virtual points)
          const realPoints = points.filter((pt) => !pt._virtual);
          const realIndex = realPoints.findIndex(
            (pt) => pt.x === points[index].x && pt.y === points[index].y
          );

          const prev = realPoints[realIndex - 1];
          const next = realPoints[realIndex + 1];

          // Clamp x so it can't pass neighbors
          if (prev && value.x <= prev.x) {
            value.x = prev.x + 1;
          }
          if (next && value.x >= next.x) {
            value.x = next.x - 1;
          }

          return value;
        },
        onDragEnd: function (
          event: any,
          datasetIndex: any,
          index: any,
          value: any
        ) {
          if (event.type === 'touchend' || event.type === 'mouseup') {
            setChartData((prev) => {
              const points = prev.datasets[0].data as ChartPoint[];

              const newPoints = points.map((pt, i) =>
                i === index - 1 ? { ...pt, x: value.x, y: value.y } : pt
              );

              const newDatasets = [...prev.datasets];

              newDatasets[datasetIndex] = {
                ...newDatasets[datasetIndex],
                data: newPoints,
              };

              return { ...prev, datasets: newDatasets };
            });
          }
        },
      },
      annotation: {
        annotations: (() => {
          const yAtZero = getAnnotationY();
          if (yAtZero == null) return {};

          return {
            verticalLine: {
              type: 'line',
              xMin: 0,
              xMax: 0,
              yMax: yAtZero,
              borderColor: theme.isDark ? '#d1d5dc' : '#4a5565',
              borderDash: [10, 5],
              borderWidth: 2,
            },
            horizontalLine: {
              type: 'line',
              yMin: yAtZero,
              yMax: yAtZero,
              xMax: 0,
              borderColor: theme.isDark ? '#d1d5dc' : '#4a5565',
              borderDash: [10, 5],
              borderWidth: 2,
            },
          };
        })(),
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'Flow Temperature (°C)',
          font: { size: 14 },
          color: theme.isDark ? '#d1d5dc' : '#4a5565',
        },
        grid: {
          color: theme.isDark ? '#4a5565' : '#d1d5dc',
        },
        min: yMin - 2,
        max: yMax + 2,
        ticks: {
          color: theme.isDark ? '#d1d5dc' : '#4a5565',
          font: { size: 14 },
        },
      },
      x: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'Outdoor Temperature (°C)',
          font: { size: 14 },
          color: theme.isDark ? '#d1d5dc' : '#4a5565',
        },
        grid: {
          color: theme.isDark ? '#4a5565' : '#d1d5dc',
        },
        min: xMin - 2,
        max: xMax + 2,
        ticks: {
          color: theme.isDark ? '#d1d5dc' : '#4a5565',
          font: { size: 14 },
        },
      },
    },
  };

  const addPoint = () => {
    setChartData((prev) => {
      // Find the two points in the current dataset
      const points = prev.datasets[0].data as ChartPoint[];
      if (points.length < 2) return prev;

      // Filter out virtual points
      const realPoints = points.filter((pt) => !pt._virtual);

      if (realPoints.length < 2) return prev;

      // Find the two central most points
      const mid = Math.floor((realPoints.length - 1) / 2);
      const first = realPoints[mid];
      const last = realPoints[mid + 1];

      // Calculate the midpoint between them
      const midPoint = {
        x: Math.round(((first.x + last.x) / 2) * 10) / 10,
        y: Math.round(((first.y + last.y) / 2) * 10) / 10,
      };

      // Insert the midpoint between first and last (sorted by x)
      const newPoints = [...points, midPoint].sort((a, b) => a.x - b.x);

      const newDatasets = [...prev.datasets];

      newDatasets[0] = {
        ...newDatasets[0],
        data: newPoints,
      };

      return { ...prev, datasets: newDatasets };
    });
  };

  const handleTableChange = (index: number, newX: number, newY: number) => {
    // Clamp and round to integer
    newX = Math.round(Math.max(xMin, Math.min(xMax, newX)));
    newY = Math.round(Math.max(yMin, Math.min(yMax, newY)));

    setChartData((prev) => {
      const points = prev.datasets[0].data as ChartPoint[];
      // Only update real points (not virtual)
      const realPoints = points.filter((pt) => !pt._virtual);

      if (index < 0 || index >= realPoints.length) return prev;

      const prevPoint = realPoints[index - 1];
      const nextPoint = realPoints[index + 1];

      if (prevPoint && newX <= prevPoint.x) {
        newX = prevPoint.x + 1;
      }
      if (nextPoint && newX >= nextPoint.x) {
        newX = nextPoint.x - 1;
      }

      const updated = realPoints.map((pt, i) =>
        i === index ? { ...pt, x: newX, y: newY } : pt
      );

      return {
        ...prev,
        datasets: [
          {
            ...prev.datasets[0],
            data: updated,
          },
        ],
      };
    });
  };

  const handleRemove = (idx: number) => {
    setChartData((prev) => {
      const points = prev.datasets[0].data as ChartPoint[];
      const realPoints = points.filter((pt) => !pt._virtual);
      if (realPoints.length <= 2) return prev; // Keep at least 2 points
      const updated = realPoints.filter((_, i) => i !== idx);
      return {
        ...prev,
        datasets: [
          {
            ...prev.datasets[0],
            data: updated,
          },
        ],
      };
    });
  };

  const { setPageTitle } = usePageTitle();

  useEffect(() => {
    setPageTitle('Weather Compensation');
  }, [setPageTitle]);

  return (
    <Card>
      <div className="mt-6">
        <Line options={options as any} data={data} />
      </div>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            onClick={addPoint}
            disabled={chartData.datasets[0].data.length >= 8}
          >
            Add Point
          </Button>
          <Button
            onClick={() =>
              addNotification(
                'success',
                'Weather compensation saved successfully!'
              )
            }
          >
            Save Changes
          </Button>
        </div>
        <div className="block overflow-x-auto overflow-y-hidden">
          <table className="min-w-full table border border-primary/40 divide-y divide-primary dark:border-primary">
            <thead className="bg-primary/40">
              <tr>
                <th className="px-3 py-3 text-left text-sm font-semibold">
                  Point Index
                </th>
                <th className="px-3 py-3 text-left text-sm font-semibold">
                  Outdoor Temp (°C)
                </th>
                <th className="px-3 py-3 text-left text-sm font-semibold">
                  Flow Temp (°C)
                </th>
                <th className="px-3 py-3 text-left text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary/40">
              {chartData.datasets[0].data.map((point, index, arr) => (
                <WeatherCompTableRow
                  key={index}
                  index={index}
                  x={point.x}
                  y={point.y}
                  onChange={handleTableChange}
                  onRemove={handleRemove}
                  disableRemove={arr.length <= 2}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
